import { useMemo } from "react";
import { classifyMessage, classifySegments, categoriesFromSubActions, categoriesFromIntents, CATEGORY_BADGES } from "@/lib/message-classification";
import { parseNarratorSegments } from "@/lib/parse-narrator-segments";
import { isHebrewText } from "@/lib/rtl-utils";

/**
 * Shared hook extracting all commonly-needed metadata fields from an
 * adventure message. Used by both MessageCard (chat) and MessageCardProse
 * to avoid duplicating the same `(msg.metadata as any)?.` chains.
 */
export function useMessageMeta(msg: any) {
  const meta = (msg.metadata ?? {}) as Record<string, any>;

  const isOOC = meta.is_ooc === true;
  const isInnerVoice = meta.is_inner_voice === true;
  const isGmQuery = meta.is_gm_query === true;
  const isSystemNote = meta.is_system_note === true;
  const msgCategory = classifyMessage(msg);
  const badge = CATEGORY_BADGES[msgCategory];
  const classification = meta.intent_classification as Record<string, any> | undefined;
  const directorNotes = meta.director_notes as Record<string, any> | undefined;
  const subActions: any[] = Array.isArray(meta.sub_actions) ? meta.sub_actions : [];
  const creditsUsed: number | undefined = meta.credits_used;
  const actionEcho = meta.action_echo as string | undefined;

  // Segment-level categories for multi-badge display
  const segmentCategories = useMemo(() => {
    if (isOOC || isInnerVoice || isGmQuery) return [];
    const secondaryIntents: string[] = Array.isArray(classification?.secondary_intents)
      ? classification.secondary_intents
      : [];
    if (msg.role === "narrator" || msg.role === "assistant") {
      const segs = parseNarratorSegments(msg.content);
      const segCats = classifySegments(segs);
      if (subActions.length > 1) {
        const saCats = categoriesFromSubActions(subActions);
        for (const cat of saCats) { if (!segCats.includes(cat)) segCats.push(cat); }
      }
      for (const cat of categoriesFromIntents(null, secondaryIntents)) {
        if (!segCats.includes(cat)) segCats.push(cat);
      }
      return segCats;
    }
    const primaryIntent = classification?.primary_intent || meta.intent;
    const cats = categoriesFromIntents(primaryIntent, secondaryIntents);
    if (subActions.length > 1) {
      for (const cat of categoriesFromSubActions(subActions)) {
        if (!cats.includes(cat)) cats.push(cat);
      }
    }
    return cats.length > 1 ? cats : [];
  }, [msg.content, msg.role, msg.metadata, classification, meta, isOOC, isInnerVoice, isGmQuery, subActions]);

  const isPlayer = msg.role === "user";
  const isNarrator = msg.role === "narrator" || msg.role === "assistant";
  const isRtl = useMemo(() => isHebrewText(msg.content), [msg.content]);
  const canTTS = isNarrator && !isOOC;

  return {
    meta,
    isOOC,
    isInnerVoice,
    isGmQuery,
    isSystemNote,
    msgCategory,
    badge,
    classification,
    directorNotes,
    subActions,
    creditsUsed,
    actionEcho,
    segmentCategories,
    hasMultipleCategories: segmentCategories.length > 1,
    isPlayer,
    isNarrator,
    isRtl,
    canTTS,
  };
}
